import { useAuth0 } from "@auth0/auth0-react";
import { Button, Checkbox, DatePicker, Dropdown, Table } from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import SuccessModal from "../../modals/SuccessModal";
import Search from "../../shared/Search";
import { getLogs } from "../../utils/audit_log";
import { getOrganizations } from "../../utils/organization";

const filterModulesValue = [
  { label: "All", value: "All" },
  { label: "Accounts", value: "Accounts" },
  { label: "Invoices", value: "Invoices" },
  { label: "Tracking Categories", value: "Tracking_categories" },
  { label: "Transfers", value: "Transfers" },
  { label: "Currencies", value: "Currencies" },
  { label: "Tax Rates", value: "Tax_rates" },
  { label: "Items", value: "Items" },
  { label: "Contacts", value: "Contacts" },
  { label: "Credit Notes", value: "Credit_notes" },
  { label: "Payments", value: "Payments" },
  { label: "Batch Payments", value: "Batch_payments" },
  { label: "Purchase Orders", value: "Purchase_orders" },
  { label: "Quotes", value: "Quotes" },
  { label: "Journals", value: "Journals" },
  { label: "Balance Sheet", value: "Balance_sheet" },
  { label: "Branding Themes", value: "Branding_themes" },
  { label: "Contact Groups", value: "Contact_groups" },
  { label: "Manual Journals", value: "Manual_journals" },
  { label: "Organisations", value: "Organisations" },
  { label: "Profit and Loss", value: "Profit_and_loss" },
  { label: "Transactions", value: "Transactions" },
  { label: "Trial Balance", value: "Trial_balance" },
  { label: "Users", value: "Users" },
  { label: "Linked Transactions", value: "Linked_transactions" },
  { label: "Prepayments", value: "Pre_payments" },
  { label: "Repeating Invoices", value: "Repeating_invoices" },
  { label: "History", value: "History" },
  { label: "Notes", value: "Notes" },
];

const disabledDate = (current) => {
  return current && current > dayjs().endOf("day"); // Disables future dates
};

const AuditLog = () => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const [fetchingData, setFetchingData] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [draw, setDraw] = useState(10);
  const [openedIndex, setOpenedIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [moduleXeroBackupDate, setModuleXeroBackupDate] = useState("");
  const [moduleXeroKeyword, setModuleXeroKeyword] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [logDate, setLogDate] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [logObject, setLogObject] = useState("");
  const [openLogPopup, setOpenLogUp] = useState(false);
  const [selectedModulesFilter, setSelectedModulesFilter] = useState([]);
  const [tempSelectedModules, setTempSelectedModules] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [moduleSearchText, setModuleSearchText] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [orgSearchText, setOrgSearchText] = useState(""); // To store the search query

  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      const token = await getIdTokenClaims();
      fetchOrgsList(token.__raw);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const token = await getIdTokenClaims();
      fetchLogs(token.__raw);
    };

    fetchData();
  }, [
    selectedModulesFilter,
    logDate,
    query,
    selectedOrg,
    currentPage,
    rowPerPage,
  ]);

  // Sync tempSelectedModules when dropdown opens
  useEffect(() => {
    if (filterVisible) {
      // When dropdown opens, initialize tempSelectedModules with selectedModulesFilter
      // Convert capitalized values back to exact format for checkboxes
      const allModules = filterModulesValue
        .filter((item) => item.value !== "All")
        .map((item) => item.value);
      
      // Convert selectedModulesFilter (capitalized) back to exact format
      const exactFormatModules = selectedModulesFilter.map((value) => {
        // Find matching module in filterModulesValue
        const module = filterModulesValue.find((item) => {
          // Compare by converting both to same case
          const itemValueLower = item.value.toLowerCase();
          const valueLower = value.toLowerCase();
          return itemValueLower === valueLower;
        });
        return module ? module.value : value;
      });
      
      // Check if all modules are selected
      const allSelected = allModules.every((module) => 
        exactFormatModules.includes(module)
      ) && exactFormatModules.length === allModules.length;
      
      if (allSelected && selectedModulesFilter.length > 0) {
        setTempSelectedModules(["All", ...allModules]);
      } else {
        setTempSelectedModules(exactFormatModules);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVisible]);

  const fetchLogs = async (token) => {
    try {
      setFetchingData(true);
      const res = await getLogs(token, {
        draw,
        page: currentPage,
        rowPerPage,
        moduleXero: tempSelectedModules,
        moduleXeroBackupDate,
        moduleXeroKeyword,
        dateRange,
        query,
        orgId: selectedOrg ? selectedOrg.id : null,
        logDate,
      });
      setTableData(res.data.aaData);
      setTotalRecords(res.data.iTotalRecords);
    } catch (e) {
      console.log(e);
    } finally {
      setFetchingData(false);
    }
  };

  const handleTempModuleChange = (checkedValues) => {
    // Get all module values except "All" - use exact values from filterModulesValue
    const allModules = filterModulesValue
      .filter((item) => item.value !== "All")
      .map((item) => item.value);
    
    const hasAll = checkedValues.includes("All");
    const previousHasAll = tempSelectedModules.includes("All");
    
    let newSelectedModules;
    
    if (hasAll && !previousHasAll) {
      // "All" was just checked - select all modules including "All"
      newSelectedModules = ["All", ...allModules];
    } else if (!hasAll && previousHasAll) {
      // "All" was just unchecked - uncheck all modules
      newSelectedModules = [];
    } else if (hasAll && checkedValues.length < allModules.length + 1) {
      // "All" is checked but some modules were unchecked - uncheck "All"
      newSelectedModules = checkedValues.filter((val) => val !== "All");
    } else {
      // Normal selection - check if all modules are selected, then auto-select "All"
      const selectedWithoutAll = checkedValues.filter((val) => val !== "All");
      if (selectedWithoutAll.length === allModules.length) {
        newSelectedModules = ["All", ...selectedWithoutAll];
      } else {
        newSelectedModules = checkedValues;
      }
    }
    
    // Don't capitalize - use exact values as they appear in filterModulesValue
    // This ensures checkbox values match exactly
    setTempSelectedModules(newSelectedModules);
  };

  const applyModuleFilters = () => {
    // Filter out "All" when applying - backend should receive all module values, not "All"
    const modulesToSend = tempSelectedModules.includes("All")
      ? tempSelectedModules.filter((val) => val !== "All")
      : tempSelectedModules;
    
    // Capitalize values for backend (if needed)
    const capitalizedValues = modulesToSend.map((value) => {
      if (value.includes("_")) {
        return value
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join("_");
      }
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    });
    
    setSelectedModulesFilter(capitalizedValues);
    setFilterVisible(false);
  };

  const handleDateChange = (date) => {
    setLogDate(date);
  };

  const handleSearchChange = useCallback(
    debounce((text) => {
      setQuery(text);
    }, 500),
    []
  );

  const handleModuleSearch = (e) => {
    setModuleSearchText(e.target.value.toLowerCase());
  };

  const filteredModules = filterModulesValue.filter((item) =>
    item.label.toLowerCase().includes(moduleSearchText)
  );

  const fetchOrgsList = async (token) => {
    try {
      const res = await getOrganizations(token, "");
      setOrganizations(res.data.accounts);
    } catch (e) {
      console.log(e);
    }
  };

  const [orgDropdownVisible, setOrgDropdownVisible] = useState(false);

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setOrgDropdownVisible(false);
  };

  const showLogPopup = (log) => {
    setLogObject(log);
    setOpenLogUp(true);
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Org. Name",
      dataIndex: "org_name",
      key: "orgName",
    },
    {
      title: "Backup Date",
      dataIndex: "log_backup_date",
      key: "backupDate",
    },
    {
      title: "Creation Date",
      dataIndex: "created_date",
      key: "creationDate",
    },
    {
      title: "Module",
      dataIndex: "xero_module",
      key: "module",
    },
    {
      title: "Log",
      dataIndex: "xero_log",
      key: "log",
      render: (_, { xero_log }) => {
        const displayLog = xero_log?.toLowerCase()?.includes("error")
          ? "Technical Error"
          : xero_log;

        return (
          <div className="text-gray-500 flex w-full">
            <div className="flex flex-row items-start">
              <div className="h-2 w-2 rounded-full bg-red-300 mt-2 mr-2 flex-shrink-0" />
              <span className="text-left">{displayLog}</span>
            </div>
          </div>
        );
      },
    },
  ];

  const mobileRender = tableData.map((item, index) => {
    return (
      <div
        key={index}
        className={
          " border rounded-lg border-gray-300 text-sm py-3 mt-3 px-4 text-gray-500"
        }
        onClick={() => {
          setOpenedIndex(index);
        }}
      >
        <div className={"flex items-center justify-between cursor-pointer"}>
          <div className={"flex items-center gap-2"}>
            <ReactSVG
              src="/assets/icons/common-dropdown.svg"
              className={`text-gray-700 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full ${
                openedIndex === index && "rotate-180"
              }`}
            />
            <div className={" text-gray-500 font-medium"}>{item.username}</div>
          </div>
          <div className={"text-gray-500 flex gap-2 items-center"}>
            <div className={`h-2 w-2 rounded-full bg-red-300`} />
            {item.xero_log}
          </div>
        </div>
        {openedIndex === index && (
          <div className={"mt-2"}>
            <div className="flex justify-between">
              <div>Org.Name:</div>
              <div>{item.org_name}</div>
            </div>
            <div className="flex justify-between">
              <div>Object:</div>
              <div>{item.xero_object?.substring(0, 20)}...</div>
            </div>
            <div className="flex justify-between">
              <div>Backup Date:</div>
              <div>{item.log_backup_date}</div>
            </div>
            <div className="flex justify-between">
              <div>Creation Date:</div>
              <div>{item.created_date}</div>
            </div>
            <div className="flex justify-between">
              <div>Module:</div>
              <div>{item.xero_module}</div>
            </div>
          </div>
        )}
      </div>
    );
  });

  const handleOrgSearch = (e) => {
    setOrgSearchText(e.target.value);
  };
  const clearOrgSelection = () => {
    setSelectedOrg(null); // Reset the selectedOrg state to null
    setOrgSearchText(""); // Optionally, clear the search text
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.account.toLowerCase().includes(orgSearchText.toLowerCase())
  );

  console.log(tableData);

  return (
    <>
      <div className={"flex items-center justify-between my-2"}>
        <div className="lg:w-4/5">
          <Button
            className={"lg:hidden mb-2 flex items-center"}
            onClick={() => {
              setShowFilter(!showFilter);
            }}
          >
            <ReactSVG src="/assets/icons/filter.svg" />
            Filter |{" "}
            {selectedModulesFilter.length +
              (selectedOrg ? 1 : 0) +
              (logDate.length ? 1 : 0)}
          </Button>
          {showFilter && (
            <div className={"lg:flex lg:flex-nowrap block items-center gap-2"}>
              {/* Module checkbox dropdown */}
              <div>
                <Dropdown
                  trigger={["click"]}
                  open={filterVisible}
                  onOpenChange={setFilterVisible}
                  dropdownRender={() => (
                    <div className="bg-white w-64 border border-gray-100 rounded-lg shadow-lg">
                      <div className="border-b border-gray-300 p-2">
                        <div className="flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2">
                          <ReactSVG
                            src="/assets/icons/search.svg"
                            className="text-gray-400"
                          />
                          <input
                            type="text"
                            className="outline-0 bg-transparent w-full"
                            onChange={handleModuleSearch}
                            placeholder="Search modules..."
                          />
                        </div>
                      </div>
                      <div className="mt-2 max-h-[300px] overflow-y-auto">
                        <Checkbox.Group
                          className="w-full flex flex-col"
                          value={tempSelectedModules}
                          onChange={handleTempModuleChange}
                        >
                          {filteredModules.map((item) => (
                            <div
                              key={item.value}
                              className="py-2 px-4 hover:bg-gray-100"
                            >
                              <Checkbox value={item.value}>
                                {item.label}
                              </Checkbox>
                            </div>
                          ))}
                        </Checkbox.Group>
                      </div>
                      <div className="border-t border-gray-300 p-2 flex justify-between">
                        <Button
                          size="small"
                          onClick={() => {
                            setTempSelectedModules([]);
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          onClick={applyModuleFilters}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                >
                  <div
                    className={
                      "w-full lg:mt-0 mt-2 cursor-pointer p-1.5 px-1.5 flex justify-between items-center rounded-lg border-gray-300 border text-medium"
                    }
                  >
                    <div className={"font-medium text-sm truncate"}>
                      {selectedModulesFilter.length > 0
                        ? `${selectedModulesFilter.length} module${
                            selectedModulesFilter.length > 1 ? "s" : ""
                          } selected`
                        : "Select Modules"}
                    </div>
                    <ReactSVG
                      src="/assets/icons/common-dropdown.svg"
                      className={"text-gray-700 flex-shrink-0 ml-1"}
                    />
                  </div>
                </Dropdown>
              </div>

              {/* Organization Dropdown */}
              <div>
                <Dropdown
                  trigger={["click"]}
                  open={orgDropdownVisible}
                  onOpenChange={setOrgDropdownVisible}
                  dropdownRender={(menu) => (
                    <div className="bg-white w-full border border-gray-100 rounded-lg shadow-lg">
                      <div className="border-b border-gray-300 p-2">
                        <div className="flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2">
                          <ReactSVG
                            src="/assets/icons/search.svg"
                            className="text-gray-400"
                          />
                          <input
                            type="text"
                            className="outline-0 bg-transparent w-full"
                            placeholder="Search organizations..."
                            value={orgSearchText}
                            onChange={handleOrgSearch} // Apply search handler
                          />
                        </div>
                      </div>
                      <div className="mt-2 max-h-[300px] overflow-y-auto">
                        {filteredOrganizations.length > 0 ? (
                          filteredOrganizations.map((item) => (
                            <div
                              key={item.id}
                              className="py-2 px-4 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                handleOrgSelect(item);
                              }}
                            >
                              {item.account}
                            </div>
                          ))
                        ) : (
                          <div className="py-2 px-4 text-gray-500">
                            No organizations found
                          </div>
                        )}
                      </div>
                      {/* Clear Selection Button */}
                      <div className="border-t border-gray-300 p-2 flex justify-between">
                        <Button size="small" onClick={clearOrgSelection}>
                          Clear Selection
                        </Button>
                      </div>
                    </div>
                  )}
                >
                  <div
                    className={
                      "w-full lg:mt-0 mt-2 cursor-pointer p-1.5 px-1.5 flex justify-between items-center rounded-lg border-gray-300 border text-medium"
                    }
                  >
                    <div className={"font-medium text-sm truncate"}>
                      {selectedOrg
                        ? selectedOrg.account
                        : "Select Organization"}
                    </div>
                    <ReactSVG
                      src="/assets/icons/common-dropdown.svg"
                      className={"text-gray-700 flex-shrink-0 ml-1"}
                    />
                  </div>
                </Dropdown>
              </div>

              <div className={" lg:mt-0 mt-2"}>
                <DatePicker
                  onChange={(_, dateStr) => handleDateChange(dateStr)}
                  disabledDate={disabledDate}
                  placeholder="Log Date"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}
        </div>
        <Search
          handleChange={(text) => handleSearchChange(text)}
          placeholder={"Search by title or description..."}
        />
      </div>

      <div className={"mt-2 pb-5 lg:block hidden"}>
        <Table
          dataSource={tableData}
          columns={columns}
          loading={isLoading || fetchingData}
          pagination={{
            current: currentPage,
            pageSize: rowPerPage,
            total: totalRecords,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            responsive: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setRowPerPage(pageSize);
            },
          }}
        />
      </div>

      <div className={"lg:hidden block"}>
        {mobileRender}

        {/* Mobile pagination controls */}
        {tableData.length > 0 && (
          <div className="flex justify-center mt-4">
            <div className="pagination-controls flex items-center gap-2">
              <Button
                disabled={currentPage === 1 || fetchingData}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of{" "}
                {Math.max(1, Math.ceil(totalRecords / rowPerPage))}
              </span>
              <Button
                disabled={
                  currentPage >= Math.ceil(totalRecords / rowPerPage) ||
                  fetchingData
                }
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <SuccessModal
        title="Log object"
        text={logObject}
        modalOpen={openLogPopup}
        onModalClose={() => setOpenLogUp(false)}
      />
    </>
  );
};

export default AuditLog;
